import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UserLayout } from '../components/User/UserLayout';
import { useAuthStore } from '../store/authStore';
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Calendar,
  DollarSign,
  Loader,
  Receipt,
  Ban,
  Printer,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  buildBillingDocumentHtml,
  formatBillingAddress,
  itemsFromLineItems,
  openBillingDocument,
  type BillingDocumentInput,
  type BillingDocumentKind,
  type RawInvoiceMeta,
  type RawLineItem,
} from '../lib/billingDocument';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface Payment {
  id: string;
  order_no: string;
  amount: number;
  currency: string;
  status: string;
  channel: string;
  product_type: string;
  product_name: string;
  subscription_plan?: string;
  created_at: string;
  updated_at: string;
  metadata?: any;
}

interface BillingInvoice {
  id: string;
  invoice_number?: string | null;
  provider?: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'refunded' | 'payment_failed';
  currency: string;
  total_amount: number;
  tax_amount?: number;
  line_items?: Array<RawLineItem & { product_id?: string }>;
  created_at: string;
  updated_at: string;
  due_at?: string | null;
  paid_at?: string | null;
  metadata?: Record<string, any>;
}

interface BillingCustomerRow {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  tax_id?: string | null;
  billing_address?: unknown;
}

type StatusKey = 'pending' | 'success' | 'failed' | 'expired';

const statusConfig: Record<StatusKey, {
  icon: typeof Clock;
  color: string;
  bg: string;
  border: string;
}> = {
  pending: {
    icon: Clock,
    color: 'text-yellow-600',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
  },
  success: {
    icon: CheckCircle,
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
  },
  failed: {
    icon: XCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
  expired: {
    icon: XCircle,
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
  },
};

const channelNames: Record<string, string> = {
  promptpay: 'PromptPay',
  truemoney: 'TrueMoney Wallet',
  linepay: 'LINE Pay',
  alipay: 'Alipay',
  wechat: 'WeChat Pay',
};

export const PaymentHistory: React.FC = () => {
  const { t, i18n } = useTranslation('billing');
  const { user, token } = useAuthStore();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [documentKind, setDocumentKind] = useState<BillingDocumentKind>('quotation');
  const [billingCustomer, setBillingCustomer] = useState<BillingCustomerRow | null>(null);

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPayments = async () => {
    try {
      // If no token, just show empty state (ProtectedRoute already handles auth)
      if (!token) {
        console.log('No token available yet');
        setPayments([]);
        setLoading(false);
        return;
      }

      console.log('Fetching payments from:', `${API_URL}/payments/my-payments`);

      const [paymentsResponse, invoicesResponse] = await Promise.all([
        fetch(`${API_URL}/payments/my-payments`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch(`${API_URL}/billing/invoices`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
      ]);

      console.log('Response status:', paymentsResponse.status);

      if (!paymentsResponse.ok) {
        const errorData = await paymentsResponse.json().catch(() => ({}));
        console.error('API Error:', errorData);

        // Only show error toast if it's a real error (not just empty data)
        if (paymentsResponse.status !== 404) {
          toast.error(t('history.toast.loadFailed'));
        }
        setPayments([]);
        return;
      }

      const data = await paymentsResponse.json();
      const invoiceData = invoicesResponse.ok ? await invoicesResponse.json() : { invoices: [] };
      const billingPayments = (invoiceData.invoices || []).map(invoiceToPayment);
      setBillingCustomer(invoiceData.customer || null);
      console.log('Payments loaded:', data);
      setPayments([...(billingPayments || []), ...(data.payments || [])]);
    } catch (error: any) {
      console.error('Fetch Payments Error:', error);
      // Silent fail - just show empty state
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const invoiceToPayment = (invoice: BillingInvoice): Payment => {
    const firstItem = invoice.line_items?.[0] || {};
    const mappedStatus: Record<BillingInvoice['status'], Payment['status']> = {
      draft: 'pending',
      open: 'pending',
      paid: 'success',
      void: 'expired',
      refunded: 'expired',
      payment_failed: 'failed',
    };

    return {
      id: invoice.id,
      order_no: invoice.invoice_number || `INV-${invoice.id.slice(0, 8)}`,
      amount: Number(invoice.total_amount || 0),
      currency: invoice.currency || 'THB',
      status: mappedStatus[invoice.status] || 'pending',
      channel: invoice.provider || 'manual',
      product_type: firstItem.product_type || invoice.metadata?.product_type || 'invoice',
      product_name: firstItem.name || invoice.metadata?.product_id || t('history.invoice.heading'),
      subscription_plan: invoice.metadata?.plan_id,
      created_at: invoice.created_at,
      updated_at: invoice.updated_at,
      metadata: {
        ...(invoice.metadata || {}),
        billingInvoiceId: invoice.id,
        billingStatus: invoice.status,
        dueAt: invoice.due_at || null,
        paidAt: invoice.paid_at || null,
        lineItems: invoice.line_items || [],
        taxAmount: Number(invoice.tax_amount || 0),
      },
    };
  };

  const formatDueDate = (dateString?: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleString(i18n.language === 'th' ? 'th-TH' : undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Bangkok',
    });
  };

  const handleViewInvoice = (payment: Payment, kind: BillingDocumentKind = 'quotation') => {
    setSelectedPayment(payment);
    setDocumentKind(payment.status === 'success' ? kind : 'quotation');
    setShowInvoice(true);
  };

  const toDocumentInput = (payment: Payment, kind: BillingDocumentKind): BillingDocumentInput => {
    const meta = (payment.metadata || {}) as Record<string, any>;
    const paidAt: string | null = meta.paidAt || (payment.status === 'success' ? payment.updated_at : null);
    const lineItems: RawLineItem[] = Array.isArray(meta.lineItems) && meta.lineItems.length > 0
      ? meta.lineItems
      : [{ name: payment.product_name, quantity: 1, amount: payment.amount, product_type: payment.product_type }];
    const customerName = billingCustomer?.name || user?.name || user?.email || '—';

    return {
      kind,
      invoiceRef: payment.order_no,
      issuedAt: payment.created_at,
      dueAt: meta.dueAt || null,
      paidAt,
      customer: {
        name: customerName,
        email: billingCustomer?.email || user?.email,
        phone: billingCustomer?.phone || undefined,
        taxId: billingCustomer?.tax_id || undefined,
        address: formatBillingAddress(billingCustomer?.billing_address) || undefined,
      },
      items: itemsFromLineItems(lineItems, meta as RawInvoiceMeta, kind === 'receipt' ? paidAt : null),
      total: payment.amount,
      extraCharge: Number(meta.taxAmount || 0),
      paymentMethod:
        payment.channel === 'manual' || !channelNames[payment.channel]
          ? 'โอนเงินเข้าบัญชีธนาคาร'
          : channelNames[payment.channel],
    };
  };

  const documentHtml = useMemo(
    () => (selectedPayment ? buildBillingDocumentHtml(toDocumentInput(selectedPayment, documentKind)) : ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPayment, documentKind, billingCustomer, user],
  );

  const handlePrintDocument = (payment: Payment, kind: BillingDocumentKind) => {
    openBillingDocument(toDocumentInput(payment, kind), { autoPrint: true });
  };

  const handleCancelInvoice = async (payment: Payment) => {
    const invoiceId = payment.metadata?.billingInvoiceId || payment.id;
    if (!invoiceId || !token) return;

    const confirmed = window.confirm(
      t('history.cancelConfirm', {
        orderNo: payment.order_no,
        defaultValue: 'ยกเลิกใบแจ้งหนี้ {{orderNo}}? หลังจากยกเลิกแล้วจะสร้างใบใหม่ได้',
      }),
    );
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_URL}/billing/invoices/${invoiceId}/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t('history.toast.cancelFailed', { defaultValue: 'ยกเลิกไม่สำเร็จ' }));
      }
      toast.success(t('history.toast.cancelled', { defaultValue: 'ยกเลิกใบแจ้งหนี้แล้ว' }));
      await fetchPayments();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('history.toast.cancelFailed');
      toast.error(message);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'th' ? 'th-TH' : undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center h-64">
          <Loader className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Receipt className="w-8 h-8 text-blue-600" />
            {t('history.title')}
          </h1>
          <p className="text-gray-600">{t('history.subtitle')}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{t('history.stats.totalPayments')}</p>
                <p className="text-2xl font-bold text-gray-900">{payments.length}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-600 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{t('history.stats.successful')}</p>
                <p className="text-2xl font-bold text-green-600">
                  {payments.filter(p => p.status === 'success').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{t('history.stats.pending')}</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {payments.filter(p => p.status === 'pending').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{t('history.stats.totalSpent')}</p>
                <p className="text-2xl font-bold text-blue-600">
                  ฿{payments
                    .filter(p => p.status === 'success')
                    .reduce((sum, p) => sum + p.amount, 0)
                    .toLocaleString()}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-600 opacity-50" />
            </div>
          </div>
        </div>

        {/* Payments List */}
        {payments.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('history.empty.title')}</h3>
            <p className="text-gray-600">{t('history.empty.hint')}</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('history.table.orderNumber')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('history.table.product')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('history.table.method')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('history.table.amount')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('history.table.status')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('history.table.date')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('history.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {payments.map((payment) => {
                    const statusKey = (payment.status in statusConfig
                      ? (payment.status as StatusKey)
                      : 'pending') as StatusKey;
                    const statusInfo = statusConfig[statusKey];
                    const StatusIcon = statusInfo.icon;

                    return (
                      <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-gray-400" />
                            <span className="font-mono text-sm text-gray-900">{payment.order_no}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{payment.product_name}</div>
                          <div className="text-xs text-gray-500">{payment.product_type}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900">
                            {channelNames[payment.channel] || payment.channel}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-semibold text-gray-900">
                            ฿{payment.amount.toLocaleString()} {payment.currency}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${statusInfo.bg} ${statusInfo.color} ${statusInfo.border}`}>
                            <StatusIcon className="w-3 h-3" />
                            {t(`history.status.${statusKey}` as const)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            {formatDate(payment.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewInvoice(payment)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title={t('history.table.viewInvoice')}
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            {payment.status === 'pending' && payment.metadata?.billingInvoiceId && (
                              <button
                                onClick={() => handleCancelInvoice(payment)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title={t('history.table.cancelInvoice', { defaultValue: 'ยกเลิกใบแจ้งหนี้' })}
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            )}
                            {payment.status === 'success' && (
                              <button
                                onClick={() => handleViewInvoice(payment, 'receipt')}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title={t('history.table.viewReceipt', { defaultValue: 'ใบเสร็จรับเงิน' })}
                              >
                                <Receipt className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Document viewer — renders the same A4 HTML that gets printed */}
        {showInvoice && selectedPayment && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-2xl w-full max-w-[900px] h-[94vh] flex flex-col overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-gray-200">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setDocumentKind('quotation')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      documentKind === 'quotation' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                    }`}
                  >
                    <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                    {t('history.invoice.quotationTab', { defaultValue: 'ใบเสนอราคา / ใบแจ้งหนี้' })}
                  </button>
                  {selectedPayment.status === 'success' && (
                    <button
                      onClick={() => setDocumentKind('receipt')}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        documentKind === 'receipt' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                      }`}
                    >
                      <Receipt className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                      {t('history.invoice.receiptTab', { defaultValue: 'ใบเสร็จรับเงิน' })}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowInvoice(false)}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                  aria-label={t('history.invoice.close')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {selectedPayment.status === 'pending' && selectedPayment.metadata?.dueAt && (
                <div className="px-4 sm:px-6 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-900 text-sm font-medium">
                  {t('history.invoice.dueNotice', {
                    due: formatDueDate(selectedPayment.metadata.dueAt),
                    defaultValue:
                      'กรุณาโอนภายในวันนี้ ก่อน {{due}} — หากไม่ชำระ ระบบจะยกเลิกใบแจ้งหนี้นี้โดยอัตโนมัติ',
                  })}
                </div>
              )}

              <iframe
                key={`${selectedPayment.id}-${documentKind}`}
                title={documentKind === 'receipt' ? 'Receipt' : 'Quotation'}
                srcDoc={documentHtml}
                className="flex-1 w-full bg-gray-200 border-0"
              />

              <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3 border-t border-gray-200">
                <button
                  onClick={() => setShowInvoice(false)}
                  className="px-5 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {t('history.invoice.close')}
                </button>
                <button
                  onClick={() => handlePrintDocument(selectedPayment, documentKind)}
                  className="px-5 py-2 bg-gray-900 hover:bg-black text-white dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <Printer className="w-4 h-4" />
                  {t('history.invoice.printButton', { defaultValue: 'พิมพ์ / บันทึกเป็น PDF' })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </UserLayout>
  );
};

